/**
 * Read a bounded, server-filtered lookup without treating a partial page as
 * the whole inventory. The caller owns the API shape and authentication.
 * @param {object} options paging dependencies and limits
 * @return {Promise<Array>} rows from the complete matching result
 */
export async function readLookupPages({fetchPage, rowsFrom, pageSize, maxPages = 4, preview = false}) {
	const rows = [];
	let offset = 0;
	let previous = null;
	for (let page = 0; page < maxPages; page += 1) {
		const document = await fetchPage(offset);
		const batch = rowsFrom(document);
		if (!Array.isArray(batch)) throw new Error('lookup_invalid_response');
		const fingerprint = JSON.stringify(batch);
		if (batch.length && fingerprint === previous) {
			throw new Error('lookup_pagination_stalled');
		}
		previous = fingerprint;
		rows.push(...batch);
		// A choice preview intentionally shows one page. Named resolution
		// must still prove completeness before concluding not-found/unique.
		if (preview) return rows;
		offset += batch.length;
		const total = Number(document?.count ?? document?.total_count);
		const hasTotal = Number.isFinite(total) && total >= 0;
		const hasNext = Boolean(document?.next);
		if (!batch.length) {
			if (hasNext || (hasTotal && offset < total)) {
				throw new Error('lookup_incomplete');
			}
			return rows;
		}
		if (!hasNext && (hasTotal ? offset >= total : batch.length < pageSize)) {
			return rows;
		}
	}
	throw new Error('lookup_incomplete');
}
